import React, { useState, useEffect, memo, Fragment, useMemo } from "react";
import { Row, Col, Form, Badge } from "react-bootstrap";
import Card from "../../../components/Card";
import DashboardService from "../../../services/pbumku/DashboardService";
import Chart from "react-apexcharts";
import CountUp from "react-countup";

const Reportppm = memo(() => {
  const [frequensi, setFrequensi] = useState([]);
  const [isNol, setIsNol] = useState(false);
  const [jumfreq, setJumfreq] = useState(0);
  const [jumVol, setJumVol] = useState(0);
  const [nilaiIDR, setNilaiIDR] = useState(0);
  const [nilaiUSD, setNilaiUSD] = useState(0);
  const [kdUPT] = useState("00.1");
  const [bentuk, setBentuk] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulanTerpilih, setBulanTerpilih] = useState("all");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agt", "Sep", "Okt", "Nov", "Des"];
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  const DashService = useMemo(() => new DashboardService(), []);

  // Logika Helper Tanggal 
  const getStartDate = () => bulanTerpilih === "all" ? new Date(selectedYear, 0, 1) : new Date(selectedYear, parseInt(bulanTerpilih), 1);
  const getEndDate = () => bulanTerpilih === "all" ? new Date(selectedYear, 11, 31) : new Date(selectedYear, parseInt(bulanTerpilih) + 1, 0);
  const formatDate = (date, isEndOfDay = false) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${isEndOfDay ? "23:59:59" : "00:00:00"}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const start = formatDate(getStartDate());
        const end = formatDate(getEndDate(), true);
        const [resFreq, resVol, resNilai, resListFreq, resBentuk] = await Promise.all([
          DashService.getJumlahFrequensi(kdUPT, start, end),
          DashService.getJumlahVolume(kdUPT, start, end),
          DashService.getJumlahNilai(kdUPT, start, end),
          DashService.getFrequensiBulan(kdUPT, start, end),
          DashService.getKetBentuk(kdUPT, start, end)
        ]);
        setJumfreq(resFreq.data);
        setJumVol(resVol.data.totalVolume);
        setNilaiIDR(resNilai.data.totalNilaiIDR);
        setNilaiUSD(resNilai.data.totalNilaiUSD);
        setFrequensi(resListFreq.data);
        setBentuk(resBentuk.data);
        setIsNol(resFreq.data === 0);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [bulanTerpilih, selectedYear]);

  // 1. COMBO CHART (FREKUENSI & VOLUME)
  const mainTrendChart = useMemo(() => {
    return {
      series: [
        { name: 'Frekuensi', type: 'column', data: frequensi.map(d => d.JUMLAH) },
        { name: 'Volume (Ton)', type: 'line', data: frequensi.map(d => (d.NETTO / 1000).toFixed(0)) }
      ],
      options: {
        chart: { height: 350, type: 'line', toolbar: { show: false } },
        stroke: { width: [0, 4], curve: 'smooth' },
        colors: ['#00E396', '#008FFB'],
        dataLabels: { enabled: true, enabledOnSeries: [1] },
        labels: frequensi.map(d => monthNames[d.BULAN - 1]),
        yaxis: [
          { title: { text: 'Frekuensi' } },
          { opposite: true, title: { text: 'Volume (Ton)' } }
        ],
        legend: { position: 'top' }
      }
    };
  }, [frequensi]);

  // 2. DONUT CHART (KOMODITI)
//   const donutChart = useMemo(() => {
//     const filteredBentuk = bentuk.filter(item => item.ket_bentuk);
//     return {
//       series: filteredBentuk.map(item => item.totalVolume / 1000),
//       options: {
//         labels: filteredBentuk.map(item => item.ket_bentuk),
//         chart: { type: 'donut' },
//         legend: { position: 'bottom' },
//         plotOptions: { pie: { donut: { size: '70%' } } },
//         colors: ['#7366ff', '#f73164', '#51bb25', '#ff9f40', '#17a2b8']
//       }
//     };
//   }, [bentuk]);

  const chartBentuk = useMemo(() => {
  if (!bentuk?.length) return null;

  const rawData = [...bentuk]
    .filter(item => item.ket_bentuk)
    .sort((a, b) => b.totalVolume - a.totalVolume);

  const topCategories = rawData.slice(0, 5);
  const otherCategories = rawData.slice(5);
  
  const finalData = [...topCategories];
  if (otherCategories.length > 0) {
    const totalOthers = otherCategories.reduce((sum, item) => sum + item.totalVolume, 0);
    finalData.push({ ket_bentuk: "Lainnya", totalVolume: totalOthers });
  }
 
  const totalVolumeAll = finalData.reduce((sum, item) => sum + (item.totalVolume / 1000), 0);

  return {
    series: finalData.map(item => item.totalVolume / 1000),
    options: {
      labels: finalData.map(item => item.ket_bentuk),
      chart: { 
        type: "donut",
        sparkline: { enabled: false } 
      },
      stroke: { width: 2, colors: ['#fff'] },
      colors: ["#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0", "#A5A5A5"],
      plotOptions: {
        pie: {
          customScale: 1, 
          donut: {
            size: "60%", 
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total Tonase",
                fontSize: "14px",
                color: "#6c757d",
                formatter: () => totalVolumeAll.toLocaleString("id-ID", { maximumFractionDigits: 1 }) + " T"
              },
              value: {
                fontSize: "22px",
                fontWeight: "bold",
                color: "#222",
                offsetY: 5
              }
            }
          }
        }
      },
      legend: {
        position: "bottom",
        fontSize: "13px",
        markers: { radius: 12 },
        itemMargin: { vertical: 5 }
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: '12px', fontWeight: 'bold' },
        dropShadow: { enabled: false }
      },
      tooltip: {
        y: { formatter: (val) => val.toLocaleString("id-ID") + " Ton" }
      }
    }
  };
}, [bentuk]);

  return (
    <Fragment>
      {/* HEADER SECTION */}
      <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm">
        <div>
          <h3 className="mb-0 fw-bold text-primary">Dashboard Ekspor SMKHP</h3>
          <p className="text-muted small mb-0">Statistik ekspor tahun {selectedYear}</p>
        </div>
        <div className="d-flex gap-2">
          <Form.Select size="sm" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="border-primary">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </Form.Select>
          <Form.Select size="sm" value={bulanTerpilih} onChange={(e) => setBulanTerpilih(e.target.value)}>
            <option value="all">Semua Bulan</option>
            {monthNames.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </Form.Select>
          {loading && <Badge bg="warning" className="d-flex align-items-center">Updating...</Badge>}
        </div>
      </div>

      {/* KPI CARDS */}
      <Row className="mb-4">
        {[
          { title: "Frekuensi", val: jumfreq, color: "primary", suffix: "", dec: 0 },
          { title: "Volume (Ton)", val: jumVol / 1000, color: "success", suffix: "", dec: 2 },
          { title: "Nilai IDR (Juta)", val: nilaiIDR / 1000000, color: "info", suffix: " M", dec: 2 },
          { title: "Nilai USD", val: nilaiUSD, color: "warning", suffix: "", dec: 2 },
        ].map((card, idx) => (
          <Col lg="3" md="6" key={idx}>
            <Card className="border-0 shadow-sm overflow-hidden">
              <Card.Body className={`border-start border-4 border-${card.color}`}>
                <p className="text-muted small mb-1 fw-bold">{card.title}</p>
                <h3 className="mb-0 fw-bold">
                  <CountUp end={card.val} decimals={card.dec} duration={2} separator="." decimal="," />
                  <span className="small text-muted" height>{card.suffix}</span>
                </h3>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* MAIN VISUALS */}
      <Row>
        <Col lg="8">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0 pt-3">
              <h5 className="mb-0">Tren Frekuensi & Volume Bulanan</h5>
            </Card.Header>
            <Card.Body>
              {frequensi.length > 0 ? (
                <Chart options={mainTrendChart.options} series={mainTrendChart.series} type="line" height={350} />
              ) : <div className="text-center py-5">Tidak ada data</div>}
            </Card.Body>
          </Card>
        </Col>

        <Col md="4">
            <Card>
            <Card.Body>
                <h5>Volume Bentuk Komoditas</h5>
                {chartBentuk ? <Chart options={chartBentuk.options} series={chartBentuk.series} type="donut"  /> : <div>Memuat...</div>}
            </Card.Body>
            </Card>
        </Col>
        {/* <Col lg="4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-transparent border-0 pt-3">
              <h5 className="mb-0">Distribusi Komoditi</h5>
            </Card.Header>
            <Card.Body className="d-flex align-items-center">
              {bentuk.length > 0 ? (
                <Chart options={donutChart.options} series={donutChart.series} type="donut" height="270" />
              ) : <div className="text-center w-100">Data komoditi kosong</div>}
            </Card.Body>
          </Card>
        </Col> */}
      </Row>
    </Fragment>
  );
});

export default Reportppm;